import { Dropdown, InputGroupText, NumericTextField, TextField, AutoCompleteDropdown } from "app/components/CustomFormElements/syncFusionFormFields";
import { addressValidationSchema } from "app/helper/validationSchema/companyValidationSchema";
import { useFormik } from "formik";
import * as Yup from "yup";
import { actionError, actionSuccess, showLoader, hideLoader } from "middleware/actions/utilityAction";
import { fetchAddress, saveAddress, editAddress, fetchSingleCompany } from "middleware/services/companyApi";
import { fetchCountry } from "middleware/services/cmsApi";
import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";
import { useHistory } from "react-router-dom";
import { debounce } from "lodash";

const CompanyBasicAddress = (props) => {
  const { companyId, addressId } = props.computedMatch.params || {};
  const [companyName, setCompanyName] = useState("");
  const [oldData, setOldData] = useState(null);
  const split_url = props.location?.pathname.split("/");

  const backPath = addressId ? `${split_url.slice(0, -3).join("/")}/edit#basic` : `${split_url.slice(0, -2).join("/")}/edit#basic`;

  const dispatch = useDispatch();
  const history = useHistory();

  const breadCrumbLinks = [
    { linkUrl: "/company", linkName: `Company`, linkActive: false },
    { linkUrl: `/company/${companyId}/edit#basic`, linkName: companyName, linkActive: false },
    { linkUrl: `/company/${companyId}/edit#basic`, linkName: "Basic", linkActive: false },
    { linkUrl: ``, linkName: "Address", linkActive: true },
  ];

  const initialFormValues = {
    address1: "",
    countryName: "",
    stateName: "",
    cityName: "",
    postCode: "",
  };

  const formik = useFormik({
    initialValues: oldData || initialFormValues,
    validationSchema: Yup.object().shape(addressValidationSchema),
    enableReinitialize: true,
  });

  const searchCountry = useCallback(
    debounce(async (e) => {
      let filters = [];
      filters.push(["countryName", "cn", e.text]);
      const response = e.text.length > 1 && (await fetchCountry({ filters: filters, fields: ["countryName"], sort: "countryName:asc", limit: 10 }));
      if (response.data && response.data.data) {
        const resData = response.data.data.map((v) => {
          return v.countryName;
        });
        if (resData) {
          e.updateData(resData);
        } else {
          e.updateData([]);
        }
      }
    }, 300),
    []
  );

  const getCompanyName = async () => {
    if (companyId) {
      const fields = { fields: ["id", "companyName"] };
      const response = await fetchSingleCompany(companyId, { ...fields });
      setCompanyName(response.data.data.companyName);
    }
  };

  // save a address
  const handleSaveAddress = () => {
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).map((field) => {
          touched[field] = true;
          return touched;
        });
        let countryNameError = "Please enter country";
        formik.setFormikState({
          ...formik,
          touched: touched,
          errors: {
            ...res,
            countryName: countryNameError,
          },
        });
      } else {
        dispatch(showLoader());
        saveAddress(companyId, formik.values)
          .then((response) => {
            dispatch(hideLoader());
            dispatch(actionSuccess("Address added successfully"));
            history.push(backPath);
          })
          .catch((err) => {
            dispatch(hideLoader());
            dispatch(actionError(err?.data?.message || "Something went wrong"));
          });
      }
    });
  };

  // handleEdit address
  const handleEditAddress = async () => {
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).map((field) => {
          touched[field] = true;
          return touched;
        });
        let countryNameError = "Please enter country";
        formik.setFormikState({
          ...formik,
          touched: touched,
          errors: {
            ...res,
            countryName: countryNameError,
          },
        });
      } else {
        dispatch(showLoader());
        const { address1, cityName, countryName, postCode, stateName } = formik.values;
        const payload = {
          address1,
          cityName,
          countryName,
          postCode,
          stateName,
        };
        if (typeof payload.postCode === "number") payload.postCode = payload.postCode.toString();
        editAddress(companyId, addressId, payload)
          .then((response) => {
            dispatch(hideLoader());
            dispatch(actionSuccess("Address updated successfully"));
            history.push(backPath);
          })
          .catch((err) => {
            dispatch(hideLoader());
            dispatch(actionError(err?.data?.message || "Something went wrong"));
          });
      }
    });
  };

  const setFormikValues = (data) => {
    const oldData = {
      address1: data.address1 || "",
      countryName: data.countryName || "",
      stateName: data.stateName || "",
      cityName: data.cityName || "",
      postCode: data.postCode || "",
    };
    setOldData(oldData);
    setTimeout(() => {
      formik.setValues(oldData);
    }, 1);
  };

  useEffect(() => {
    if (props.editData && companyId && addressId) {
      formik.resetForm();
      fetchAddress(companyId)
        .then((res) => {
          setFormikValues(res.data?.data?.companyLocation?.filter((e) => e.id === addressId)?.[0] || {});
        })
        .catch((err) => {
          dispatch(actionError(err?.data?.message || "Something went wrong"));
        });
    } else {
      formik.resetForm();
    }
  }, [props.editData, companyId, addressId]);

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));
  }, [breadCrumbLinks]);

  useEffect(() => {
    getCompanyName();
  }, [companyId]);

  return (
    <div className="p-0 m-3 company-basic-modal">
      <div className="modal-header">Company - Address</div>
      <div className="modal-body">
        <div className="row pl-3 pt-3 pr-3">
          <div className="col-md-12">
            <div className="form-group required">
              <TextField
                formik={formik}
                type="text"
                multiline={true}
                numberCount={true}
                maxLength={1000}
                rows={5}
                name="address1"
                className="form-control"
                id="address1"
                placeholder="Enter Address *"
              />
            </div>
          </div>
        </div>
        <div className="row pl-3 pr-3">
          <div className="col-md-4">
            <div className="form-group required">
              <AutoCompleteDropdown
                formik={formik}
                placeholder="Country *"
                id="countryName"
                name="countryName"
                autocomplete="off"
                // value={formik.values.country}
                allowFiltering={true}
                filtering={searchCountry}
                autofill={true}
                allowCustom={props.editData && formik.values.countryName}
                filterType="StartsWith"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <TextField formik={formik} type="text" className="form-control" id="state" placeholder="Enter State" name="stateName" />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <TextField formik={formik} type="text" className="form-control" id="city" placeholder="Enter City" name="cityName" />
            </div>
          </div>
        </div>
        <div className="row pl-3 pr-3">
          <div className="col-md-4">
            <div className="form-group">
              <TextField formik={formik} type="text" className="form-control" id="zip" placeholder="Enter Zip" name="postCode" />
            </div>
          </div>
        </div>

        <div className="text-right border-top p-3 mt-auto">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              history.push(backPath);
            }}
            className="btn btn-outline-dark mr-3"
          >
            Cancel
          </button>

          {true ? (
            <button type="button" className="btn btn-primary" onClick={props.editData ? handleEditAddress : handleSaveAddress} disabled={false}>
              {props?.editData ? "Update" : "Save"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CompanyBasicAddress;
